import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface LegalDocPreviewDialogProps {
  slug: string;
  title?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface DocData {
  title: string;
  version: string;
  effective_at: string;
  content_md: string;
}

export function LegalDocPreviewDialog({ slug, title, open, onOpenChange }: LegalDocPreviewDialogProps) {
  const [doc, setDoc] = useState<DocData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase
        .from('legal_documents')
        .select('title, version, effective_at, content_md')
        .eq('slug', slug)
        .eq('locale', 'pt-BR')
        .is('superseded_at', null)
        .order('effective_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setError('Documento indisponível.');
      } else {
        setDoc(data as DocData);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, slug]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{doc?.title ?? title ?? 'Documento legal'}</DialogTitle>
          {doc && (
            <DialogDescription>
              Versão {doc.version} · Em vigor desde{' '}
              {new Date(doc.effective_at).toLocaleDateString('pt-BR')}
            </DialogDescription>
          )}
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <p className="text-sm text-muted-foreground py-8 text-center">{error}</p>
        ) : doc ? (
          <ScrollArea className="h-[60vh] border rounded-md p-4">
            <article className="prose prose-sm prose-neutral dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.content_md}</ReactMarkdown>
            </article>
          </ScrollArea>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
